// Installing the staff app (PWA) on any device. Chrome, Edge, Samsung Internet
// and Opera fire `beforeinstallprompt`, which we keep so our own Install button
// can open the browser's dialog. Safari (iPhone, iPad, Mac) and Firefox have no
// such event: for them we show the steps for that browser instead.

/** The browser's install dialog, kept from `beforeinstallprompt` until the user taps Install. */
export type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((f) => f());

/** Call once at start-up, before React renders: the event can fire before any component mounts. */
export function listenForInstall() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    try {
      localStorage.setItem(INSTALLED_KEY, "1");
    } catch {
      // Private mode: display-mode still tells us once it opens installed.
    }
    notify();
  });
}

export const installEvent = () => deferred;
export function onInstallChange(f: () => void) {
  listeners.add(f);
  return () => void listeners.delete(f);
}

/** Opens the browser's own install dialog; true if the person accepted. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  notify();
  return outcome === "accepted";
}

const INSTALLED_KEY = "savon-installed";

/** Running as the installed app (home screen, dock, app window), not in a browser tab. */
export function runningInstalled(): boolean {
  if (typeof window === "undefined") return false;
  const standalone = ["standalone", "fullscreen", "minimal-ui", "window-controls-overlay"].some((m) => window.matchMedia?.(`(display-mode: ${m})`).matches);
  return standalone || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export type Platform =
  | "in-app" // Facebook, Messenger, Instagram, WhatsApp, LINE, WeChat or an Android WebView: cannot install from here
  | "ios-safari"
  | "ios-other" // Chrome, Edge or Firefox on iPhone/iPad (Add to Home Screen through the share sheet, iOS 16.4+)
  | "android-chromium"
  | "android-samsung"
  | "android-firefox"
  | "mac-safari"
  | "desktop-chromium"
  | "desktop-firefox"
  | "other";

/** Which install steps apply, from the user agent (iPadOS reports a Mac, so touch points tell them apart). */
export function detectPlatform(ua: string, maxTouchPoints = 0): Platform {
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  const android = /Android/.test(ua);
  if (/FBAN|FBAV|FB_IAB|Instagram|Messenger|WhatsApp|Line\/|MicroMessenger|; wv\)/.test(ua)) return "in-app";
  if (ios) return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? "ios-other" : "ios-safari";
  if (android) {
    if (/SamsungBrowser/.test(ua)) return "android-samsung";
    if (/Firefox/.test(ua)) return "android-firefox";
    return "android-chromium";
  }
  if (/Firefox/.test(ua)) return "desktop-firefox";
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua)) return "mac-safari";
  if (/Chrome|Chromium|Edg\//.test(ua)) return "desktop-chromium";
  return "other";
}

/** Step-by-step install text for browsers without (or before) an install dialog, in Bangla and English. */
export function installSteps(p: Platform): { bn: string; en: string } {
  switch (p) {
    case "in-app":
      return {
        bn: "এই অ্যাপের ভেতরের ব্রাউজার থেকে ইনস্টল হয় না। লিংকটি কপি করে Chrome (Android) বা Safari (iPhone) এ খুলুন।",
        en: "This in-app browser can't install apps. Copy the link and open it in Chrome (Android) or Safari (iPhone).",
      };
    case "ios-safari":
      return { bn: "নিচের শেয়ার বোতাম (⬆️) চাপুন, তারপর \"Add to Home Screen\" চাপুন।", en: "Tap the Share button (⬆️), then \"Add to Home Screen\"." };
    case "ios-other":
      return { bn: "শেয়ার বোতাম (⬆️) চাপুন, তারপর \"Add to Home Screen\"। না দেখালে Safari তে খুলুন।", en: "Tap Share (⬆️), then \"Add to Home Screen\". If it isn't there, open this page in Safari." };
    case "android-chromium":
      return { bn: "উপরের ⋮ মেনু চাপুন, তারপর \"Install app\" বা \"Add to Home screen\"।", en: "Tap the ⋮ menu, then \"Install app\" or \"Add to Home screen\"." };
    case "android-samsung":
      return { bn: "নিচের ☰ মেনু চাপুন, তারপর \"Add page to\" → \"Home screen\"।", en: "Tap the ☰ menu, then \"Add page to\" → \"Home screen\"." };
    case "android-firefox":
      return { bn: "⋮ মেনু চাপুন, তারপর \"Install\" বা \"Add to Home screen\"।", en: "Tap the ⋮ menu, then \"Install\" or \"Add to Home screen\"." };
    case "mac-safari":
      return { bn: "উপরের মেনুতে File → \"Add to Dock\" চাপুন।", en: "In the menu bar choose File → \"Add to Dock\"." };
    case "desktop-chromium":
      return { bn: "ঠিকানা বারের ডান পাশে ইনস্টল আইকন (⊕) চাপুন, বা ⋮ মেনু → \"Install SAVON\"।", en: "Click the install icon (⊕) at the right of the address bar, or ⋮ menu → \"Install SAVON\"." };
    case "desktop-firefox":
      return { bn: "Firefox এ ইনস্টল হয় না। Chrome বা Edge এ এই পাতা খুলে ইনস্টল করুন।", en: "Firefox on a computer can't install apps. Open this page in Chrome or Edge to install it." };
    default:
      return { bn: "ব্রাউজারের মেনু থেকে \"Install\" বা \"Add to Home screen\" বেছে নিন।", en: "Use your browser's menu: \"Install\" or \"Add to Home screen\"." };
  }
}

export type Role = "ops" | "pharmacy" | "rider";

const ROLE_APP: Record<Role, { manifest: string; title: string }> = {
  ops: { manifest: "/manifest-ops.webmanifest", title: "SAVON Ops" },
  pharmacy: { manifest: "/manifest-pharmacy.webmanifest", title: "SAVON Pharmacy" },
  rider: { manifest: "/manifest-rider.webmanifest", title: "SAVON Rider" },
};

/**
 * Points the page at the signed-in role's manifest, so the installed app has that
 * role's name, start screen and shortcuts ("SAVON Rider" opens on today's run).
 * Signed out, it is the shared "SAVON Staff" app.
 */
export function setRoleApp(role: Role | null) {
  if (typeof document === "undefined") return;
  const app = role ? ROLE_APP[role] : { manifest: "/manifest.webmanifest", title: "SAVON Staff" };
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (link && link.getAttribute("href") !== app.manifest) link.setAttribute("href", app.manifest);
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", app.title);
}

const SNOOZE_KEY = "savon-install-snooze";
/** "Later" hides the prompt for a day; it comes back on every device until the app is installed. */
export const SNOOZE_MS = 24 * 3_600_000;

export function snoozedUntil(): number {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY) ?? 0);
  } catch {
    return 0;
  }
}
export function snooze(now = Date.now()) {
  try {
    localStorage.setItem(SNOOZE_KEY, String(now + SNOOZE_MS));
  } catch {
    // Private mode: it shows again next visit.
  }
}
